import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, Check, Minus, Info, LockSimple, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SectionHeader } from "../components/shared";
import { Button } from "../components/ui/button";

const ROLE_LABEL = { admin: "Administrator", accountant: "Accountant", sales: "Sales", purchasing: "Purchasing", viewer: "Viewer" };

const LOCKED = [
  { label: "Approve / decline purchase orders", value: "approver" },
  { label: "Administration (company, locations, bank, approvals, data integrity, cleanup)" },
  { label: "Manage team members & assign roles" },
  { label: "Edit this permissions matrix & view audit log" },
];

function Toggle({ on, onClick, disabled, testid }) {
  return (
    <button
      onClick={onClick} disabled={disabled} data-testid={testid} aria-pressed={on}
      className={`mx-auto flex h-7 w-7 items-center justify-center rounded-sm border transition-colors ${on ? "border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/20" : "border-border hover:border-primary hover:bg-primary/5"} disabled:cursor-not-allowed disabled:opacity-60`}
    >
      {on ? <Check size={16} weight="bold" className="text-emerald-600" /> : <Minus size={14} className="text-muted-foreground/50" />}
    </button>
  );
}

function LockedRow({ row, roles }) {
  return (
    <tr className="border-b border-border/60 bg-secondary/20" data-testid="capability-row-locked">
      <td className="px-4 py-2.5 font-medium"><span className="inline-flex items-center gap-1.5"><LockSimple size={13} className="text-muted-foreground" />{row.label}</span></td>
      <td className="px-4 py-2.5 text-center"><Check size={17} weight="bold" className="text-emerald-600 mx-auto" /></td>
      {roles.map((r) => (
        <td key={r} className="px-4 py-2.5 text-center">
          {row.value === "approver" ? <span title="Only when assigned as the approver for that PO" className="text-amber-600 text-xs font-semibold">If assigned</span> : <Minus size={15} className="text-muted-foreground/40 mx-auto" />}
        </td>
      ))}
    </tr>
  );
}

export default function RoleMatrix() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["permissions"], queryFn: async () => (await api.get("/permissions")).data });
  const save = useMutation({
    mutationFn: async (perms) => (await api.put("/permissions", { perms })).data,
    onSuccess: (d) => { qc.setQueryData(["permissions"], d); toast.success("Permissions updated"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const roles = data?.roles || [];
  const perms = data?.perms || {};
  const toggle = (cap, role) => {
    const cur = perms[cap] || [];
    save.mutate({ [cap]: cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role] });
  };
  const resetDefaults = () => save.mutate(Object.fromEntries((data?.caps || []).map((c) => [c.key, c.default])));
  const groups = (data?.caps || []).reduce((acc, c) => { (acc[c.group] = acc[c.group] || []).push(c); return acc; }, {});
  const span = roles.length + 2;

  return (
    <div className="animate-fade-up" data-testid="role-matrix-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <SectionHeader title="Roles & Permissions" subtitle={isAdmin ? "Click any cell to grant or revoke a capability. Changes apply immediately." : "Exactly what each role can do in Neuclius"} />
        {isAdmin && <Button variant="outline" size="sm" onClick={resetDefaults} disabled={save.isPending || !data} className="rounded-sm h-8 text-xs gap-1.5" data-testid="permissions-reset"><ArrowCounterClockwise size={14} /> Reset to defaults</Button>}
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="permissions-table">
          <thead>
            <tr className="bg-secondary/50 border-b border-border">
              <th className="text-left px-4 py-3 font-semibold min-w-[20rem]">Capability</th>
              {["admin", ...roles].map((r) => <th key={r} className="px-4 py-3 font-semibold text-center whitespace-nowrap" data-testid={`role-col-${r}`}>{ROLE_LABEL[r] || r}</th>)}
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={span || 6} className="py-10 text-center text-muted-foreground">Loading…</td></tr>}
            {Object.entries(groups).map(([group, caps]) => [
              <tr key={group} className="bg-secondary/30 border-b border-border"><td colSpan={span} className="px-4 py-1.5 text-[11px] uppercase tracking-wider font-bold text-muted-foreground">{group}</td></tr>,
              ...caps.map((c) => (
                <tr key={c.key} className="border-b border-border/60 hover:bg-secondary/30" data-testid={`capability-row-${c.key}`}>
                  <td className="px-4 py-2 font-medium">{c.label}</td>
                  <td className="px-4 py-2 text-center"><Check size={17} weight="bold" className="text-emerald-600 mx-auto" /></td>
                  {roles.map((r) => (
                    <td key={r} className="px-4 py-2 text-center">
                      <Toggle on={(perms[c.key] || []).includes(r)} disabled={!isAdmin || save.isPending} onClick={() => toggle(c.key, r)} testid={`perm-toggle-${c.key}-${r}`} />
                    </td>
                  ))}
                </tr>
              )),
            ])}
            {data && <tr className="bg-secondary/30 border-b border-border"><td colSpan={span} className="px-4 py-1.5 text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Administrator only</td></tr>}
            {data && LOCKED.map((row) => <LockedRow key={row.label} row={row} roles={roles} />)}
          </tbody>
        </table>
      </div>

      <div className="mt-5 space-y-2 text-sm text-muted-foreground">
        <p className="flex items-start gap-2"><ShieldCheck size={16} className="text-primary mt-0.5 shrink-0" weight="fill" /> <span><strong className="text-foreground">Administrators</strong> always keep every permission (so no one can be locked out) and <strong className="text-foreground">bypass purchase-order approval thresholds</strong>.</span></p>
        <p className="flex items-start gap-2"><LockSimple size={16} className="text-muted-foreground mt-0.5 shrink-0" weight="fill" /> <span>Locked rows stay with administrators to prevent privilege escalation.</span></p>
        <p className="flex items-start gap-2"><Info size={16} className="text-amber-600 mt-0.5 shrink-0" weight="fill" /> <span><strong className="text-foreground">"If assigned"</strong> means a user can approve or decline a specific purchase order only when set as its approver in Administration → Approvals.</span></p>
      </div>
    </div>
  );
}
